import { useMutation } from 'react-query'
import { BASE_API_URL, WEB_APP_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrorsAndJSON } from 'utils/handle-errors'
import { LineItem } from 'interfaces/line-item'

type Data = {
  stripe_checkout_link: {
    link: string
  }
}

const fetchCreateStripeCheckoutLink = async ({
  lineItems,
  couponId,
  saleRepId,
  hubspotDealLink,
  mode,
  stripeSuccessUrl,
  hasTrial,
  collectShippingAddress,
  allowCustomerUseReferralCode,
  sandboxId,
  sandboxOrgId,
  sandboxCustomerType,
  sandboxPhonePreference,
  usePhoneFromSandboxId,
  cliConversion,
  cliPartnerIntegrationId,
  productName,
}: {
  lineItems: Partial<LineItem>[]
  couponId: string | number | undefined
  saleRepId: string | number | undefined
  hubspotDealLink: string | undefined
  mode: 'payment' | 'subscription'
  stripeSuccessUrl?: string
  hasTrial?: boolean
  collectShippingAddress?: boolean
  allowCustomerUseReferralCode?: boolean
  sandboxId?: number
  sandboxOrgId?: number
  sandboxCustomerType?: 'infrastructure' | 'sales_enablement'
  sandboxPhonePreference?: 'same' | 'different'
  usePhoneFromSandboxId?: number
  cliConversion?: boolean
  cliPartnerIntegrationId?: number
  productName?: 'Sales Enablement' | 'One' | 'Infrastructure'
}) => {
  const requestUrl = `${BASE_API_URL}/admin/api/v1/stripe_checkout_links`
  let response = await fetch(requestUrl, {
    method: API_METHODS.POST,
    headers: apiHeaders(store.getState()),
    body: JSON.stringify({
      line_items: lineItems,
      coupon_id: couponId,
      sale_rep_id: saleRepId,
      stripe_success_url:
        stripeSuccessUrl ||
        `${WEB_APP_URL}/teams/stripe-success/?session_id={CHECKOUT_SESSION_ID}`,
      stripe_cancel_url: 'https://linqapp.com/linq-one',
      ...(hubspotDealLink && { hubspot_deal_link: hubspotDealLink }),
      mode,
      shipping_address_collection: collectShippingAddress,
      ...(productName && {
        product_type: productName === 'One' ? 'Legacy' : productName,
      }),
      ...(hasTrial && { trial_days: 14 }),
      ...(collectShippingAddress && { shipping_address_collection: true }),
      ...(allowCustomerUseReferralCode && {
        allow_promotion_codes: true,
      }),
      ...(sandboxId && { sandbox_id: sandboxId }),
      ...(sandboxOrgId && { sandbox_org_id: sandboxOrgId }),
      ...(sandboxCustomerType && {
        sandbox_customer_type: sandboxCustomerType,
      }),
      ...(sandboxPhonePreference && {
        sandbox_phone_preference: sandboxPhonePreference,
      }),
      ...(usePhoneFromSandboxId && {
        use_phone_from_sandbox_id: usePhoneFromSandboxId,
      }),
      ...(cliConversion && { cli_conversion: true }),
      ...(cliPartnerIntegrationId && {
        cli_partner_integration_id: cliPartnerIntegrationId,
      }),
    }),
  })
  response = await handleErrorsAndJSON(response)
  const json: Data = await response.json()
  return json.stripe_checkout_link
}

export const useCreateStripeCheckoutLink = () => {
  return useMutation(
    ({
      lineItems,
      couponId,
      saleRepId,
      hasTrial,
      hubspotDealLink,
      mode,
      collectShippingAddress,
      stripeSuccessUrl,
      allowCustomerUseReferralCode,
      sandboxId,
      sandboxOrgId,
      sandboxCustomerType,
      sandboxPhonePreference,
      usePhoneFromSandboxId,
      cliConversion,
      cliPartnerIntegrationId,
      productName,
    }: {
      lineItems: Partial<LineItem>[]
      couponId: string | number | undefined
      saleRepId: string | number | undefined
      hasTrial: boolean
      hubspotDealLink: string | undefined
      mode: 'payment' | 'subscription'
      collectShippingAddress?: boolean
      stripeSuccessUrl?: string
      allowCustomerUseReferralCode?: boolean
      sandboxId?: number
      sandboxOrgId?: number
      sandboxCustomerType?: 'infrastructure' | 'sales_enablement'
      sandboxPhonePreference?: 'same' | 'different'
      usePhoneFromSandboxId?: number
      cliConversion?: boolean
      cliPartnerIntegrationId?: number
      productName?: 'Sales Enablement' | 'One' | 'Infrastructure'
    }) =>
      fetchCreateStripeCheckoutLink({
        lineItems,
        couponId,
        saleRepId,
        hubspotDealLink,
        mode,
        stripeSuccessUrl,
        hasTrial,
        collectShippingAddress,
        allowCustomerUseReferralCode,
        sandboxId,
        sandboxOrgId,
        sandboxCustomerType,
        sandboxPhonePreference,
        usePhoneFromSandboxId,
        cliConversion,
        cliPartnerIntegrationId,
        productName,
      })
  )
}
